import React, { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';

import { IconGrid, IconFinance, IconUpload, IconPlus, IconBox } from '../../../assets/svg/icons';
import Button from '../../../components/atoms/Button';
import { ButtonKind } from '../../../components/atoms/Button/enums';
import ScPageHeader from '../../../components/molecules/ScPageHeader';
import ScCard from '../../../components/atoms/ScCard';
import ScSectionHeader from '../../../components/molecules/ScSectionHeader';
import ScDonut from '../../../components/molecules/ScDonut';
import Loader from '../../../components/atoms/Loader';
import { LoaderMode } from '../../../components/atoms/Loader/enums';
import routes from '../../../setup/consts/routes';
import useHeaderTitleContext from '../../../hooks/useHeaderTitleContext';
import { useWarehouseReport } from './hooks';

const CHART_PALETTE = [
  'var(--brand-green)',
  '#4A78D6',
  '#FBB03B',
  '#8B5CD6',
  '#D47974',
  '#2BA3A8',
];

const ReportPage: React.FC = () => {
  const intl = useIntl();
  const history = useHistory();
  const { data, isLoading } = useWarehouseReport();
  const { setHeaderTitle } = useHeaderTitleContext();

  useEffect(() => {
    setHeaderTitle(
      intl.formatMessage({
        id: 'mainNavigation.warehouse',
        defaultMessage: 'Warehouse',
      })
    );
  }, [intl, setHeaderTitle]);

  const packed = data?.packedPercentage ?? 0;
  const forPacking = data?.forPackingPercentage ?? 0;

  const citySegments = (data?.ordersStatePercentage ?? []).slice(0, 6).map((o, i) => ({
    value: o.ordersPercentage,
    color: CHART_PALETTE[i % CHART_PALETTE.length],
    label: o.state,
  }));

  const topItems = data?.ordersGroupingByItem ?? [];

  return (
    <>
      <ScPageHeader
        title={intl.formatMessage({ id: 'mainNavigation.warehouse', defaultMessage: 'Warehouse' })}
        subtitle={intl.formatMessage({ id: 'report.subtitle', defaultMessage: 'Packing efficiency and sales analytics' })}
        actions={
          <>
            <div style={{ display: 'flex', gap: 4, padding: 3, borderRadius: 'var(--r-md)', background: 'var(--paper-2)' }}>
              <button
                onClick={() => history.push(routes.WAREHOUSE)}
                style={{
                  padding: '7px 12px', borderRadius: 6, border: 0, fontFamily: 'inherit',
                  background: 'transparent', color: 'var(--muted)',
                  fontSize: 'var(--t-sm)', fontWeight: 600, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}
              >
                <IconGrid style={{ width: 14, height: 14 }} />
                {intl.formatMessage({ id: 'warehouse.inventoryTab', defaultMessage: 'Inventory' })}
              </button>
              <button style={{
                padding: '7px 12px', borderRadius: 6, border: 0, fontFamily: 'inherit',
                background: 'var(--surface)', boxShadow: 'var(--sh-1)',
                color: 'var(--ink)', fontSize: 'var(--t-sm)', fontWeight: 600, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                <IconFinance style={{ width: 14, height: 14 }} />
                {intl.formatMessage({ id: 'warehouse.analyticsTab', defaultMessage: 'Analytics' })}
              </button>
            </div>
            <Button kind={ButtonKind.Outline} icon={IconUpload}>
              {intl.formatMessage({ id: 'warehouse.importSkus', defaultMessage: 'Import SKUs' })}
            </Button>
            <Button kind={ButtonKind.Primary} icon={IconPlus}>
              {intl.formatMessage({ id: 'warehouse.addProduct', defaultMessage: 'Add product' })}
            </Button>
          </>
        }
      />

      {isLoading ? (
        <Loader mode={LoaderMode.WarehouseReport} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-4)' }}>
          {/* Row 1: Packing efficiency + Sales by city */}
          <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 'var(--s-4)' }}>
            {/* Packing efficiency */}
            <ScCard>
              <ScSectionHeader title={intl.formatMessage({ id: 'report.packingEfficiency', defaultMessage: 'Packing efficiency' })} subtitle={intl.formatMessage({ id: 'report.lastSevenDays', defaultMessage: 'Last 7 days' })} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s-7)', padding: 'var(--s-5) 0 var(--s-3)' }}>
                <ScDonut
                  size={180}
                  thickness={18}
                  segments={[
                    { value: packed, color: 'var(--brand-green)' },
                    { value: Math.max(0, 100 - packed - forPacking), color: 'var(--paper-2)' },
                    ...(forPacking > 0 ? [{ value: forPacking, color: 'var(--muted-3)' }] : []),
                  ]}
                  center={
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 'var(--t-4xl)', fontWeight: 700, letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
                        {Math.round(packed)}
                        <span style={{ fontSize: 17, fontWeight: 400, color: 'var(--muted)', marginLeft: 1 }}>%</span>
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>{intl.formatMessage({ id: 'report.packed', defaultMessage: 'Packed' })}</div>
                    </div>
                  }
                />
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {[
                    { label: intl.formatMessage({ id: 'report.packed', defaultMessage: 'Packed' }), color: 'var(--brand-green)', pct: packed },
                    { label: intl.formatMessage({ id: 'report.pendingPacking', defaultMessage: 'Pending packing' }), color: 'var(--muted-2)', pct: forPacking },
                    { label: intl.formatMessage({ id: 'report.returnedToShelf', defaultMessage: 'Returned to shelf' }), color: 'var(--warning)', pct: 0 },
                  ].map(({ label, color, pct }) => (
                    <div key={label}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink-2)' }}>{label}</span>
                        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--muted)', fontVariantNumeric: 'tabular-nums' }}>
                          {pct.toFixed(1)}%
                        </span>
                      </div>
                      <div style={{ height: 6, borderRadius: 3, background: 'var(--paper-2)', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 3 }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {packed >= 95 && (
                <div style={{ padding: '12px 14px', background: 'var(--success-soft)', borderRadius: 'var(--r-md)', color: 'var(--success-ink)', fontSize: 'var(--t-sm)' }}>
                  <strong>{intl.formatMessage({ id: 'report.onTrack', defaultMessage: "You're on track." })}</strong> {intl.formatMessage({ id: 'report.noBacklog', defaultMessage: 'No packing backlog — all items packed successfully.' })}
                </div>
              )}
            </ScCard>

            {/* Sales by city */}
            <ScCard>
              <ScSectionHeader title={intl.formatMessage({ id: 'report.salesByCity', defaultMessage: 'Sales by city' })} subtitle={intl.formatMessage({ id: 'report.shippedUnitsSubtitle', defaultMessage: 'Share of shipped units' })} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s-5)' }}>
                <ScDonut
                  size={160}
                  thickness={18}
                  segments={citySegments.length > 0 ? citySegments : [{ value: 1, color: 'var(--paper-2)' }]}
                  center={
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 20, fontWeight: 700, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
                        {citySegments.length}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{intl.formatMessage({ id: 'report.cities', defaultMessage: 'cities' })}</div>
                    </div>
                  }
                />
                <div style={{ flex: 1 }}>
                  {citySegments.map(({ label, color, value }, i) => (
                    <div
                      key={label || i}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '6px 0',
                        borderBottom: i < citySegments.length - 1 ? '1px solid var(--line)' : 'none',
                      }}
                    >
                      <div style={{ width: 8, height: 8, borderRadius: 2, background: color, flexShrink: 0 }} />
                      <span style={{ flex: 1, fontSize: 13, color: 'var(--ink-2)' }}>{label}</span>
                      <span style={{ fontSize: 13, color: 'var(--muted)', fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
                        {value.toFixed(1)}%
                      </span>
                    </div>
                  ))}
                  {citySegments.length === 0 && (
                    <p style={{ fontSize: 'var(--t-sm)', color: 'var(--muted)', margin: 0 }}>{intl.formatMessage({ id: 'report.noCityData', defaultMessage: 'No city data available.' })}</p>
                  )}
                </div>
              </div>
            </ScCard>
          </div>

          {/* Row 2: Top selling items */}
          <ScCard>
            <ScSectionHeader title={intl.formatMessage({ id: 'report.topSellingItems', defaultMessage: 'Top selling items' })} subtitle={intl.formatMessage({ id: 'report.topItemsSubtitle', defaultMessage: 'By units moved, last 7 days' })} />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 'var(--s-4)' }}>
              {topItems.slice(0, 6).map((item, i) => (
                <div
                  key={item.sku || i}
                  style={{
                    display: 'flex', gap: 14, padding: 'var(--s-4)',
                    borderRadius: 'var(--r-md)', background: 'var(--paper)',
                    border: '1px solid var(--line)',
                  }}
                >
                  <div style={{
                    width: 56, height: 56, borderRadius: 'var(--r-sm)',
                    background: 'var(--paper-2)', color: 'var(--muted-2)',
                    display: 'grid', placeItems: 'center', flexShrink: 0, overflow: 'hidden',
                  }}>
                    {item.coverImage ? (
                      <img src={item.coverImage} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <IconBox style={{ width: 24, height: 24 }} />
                    )}
                  </div>
                  <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                    <div
                      style={{ fontWeight: 600, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      className='ar'
                    >
                      {item.name}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--font-mono, monospace)', marginTop: 2 }}>
                      {item.sku}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 'auto', paddingTop: 6 }}>
                      <span style={{ fontWeight: 700, fontSize: 17, fontVariantNumeric: 'tabular-nums' }}>
                        {item.quantity.toLocaleString()}
                      </span>
                      <span style={{ fontSize: 11, color: 'var(--muted)' }}>{intl.formatMessage({ id: 'report.pcs', defaultMessage: 'pcs' })}</span>
                      <span style={{
                        marginLeft: 'auto', padding: '2px 8px', borderRadius: 999,
                        background: i === 0 ? 'var(--success-soft)' : 'var(--paper-2)',
                        color: i === 0 ? 'var(--success-ink)' : 'var(--muted)',
                        fontSize: 11, fontWeight: 600,
                      }}>
                        #{i + 1}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
              {topItems.length === 0 && (
                <div style={{ gridColumn: '1/-1', textAlign: 'center', color: 'var(--muted)', padding: 'var(--s-6)', fontSize: 'var(--t-sm)' }}>
                  {intl.formatMessage({ id: 'report.noSalesData', defaultMessage: 'No sales data available yet.' })}
                </div>
              )}
            </div>
          </ScCard>
        </div>
      )}
    </>
  );
};

export default ReportPage;
